import { useMemo } from 'react';
import { useAllocationWarnings } from '../allocation-tracts-table/hooks/use-allocation-warnings';
import {
  AllocationTotals,
  AllocationWarning,
} from 'pages/development-areas/show/allocation-manager-types';
import { ComparisonAgreement } from './types';

export type AllocationMethod = 'acreage' | 'lateral_length';

/** One tract's row in a column's Tract Allocation table, for the selected method. */
export interface ComparisonTractRow {
  tractName: string | null;
  value: number | null;
  factor: number | null;
  gisValue: number | null;
  geometry: GeoJSON.Geometry | null;
}

/** Everything one side of the comparison renders. */
export interface ComparisonColumn {
  agreement: ComparisonAgreement | null;
  method: AllocationMethod;
  /** 'ac' or 'ft' — the unit the selected method measures in. */
  unitLabel: string;
  rows: ComparisonTractRow[];
  totalValueSum: number | null;
  totalGisValueSum: number | null;
  totalFactor: number | null;
  /** The unit's GIS figure (`development_area.acreage`), not the plat figure. */
  sourcedValue: number | null;
  /** The plat/sourced figure the variance is measured against. */
  platValue: number | null;
  /**
   * Percent the unit's GIS figure deviates from the plat figure — the same pair of
   * operands as the GIS validation tag beside it.
   */
  variancePercent: number | null;
  /**
   * GIS validation alerts, from the same hook the unit page's Allocation Manager
   * table uses — the modal must not invent a second set of thresholds.
   */
  warnings: AllocationWarning[];
}

/**
 * Derives one comparison column from an agreement and the method to display it under.
 *
 * Call this a fixed number of times (once per column), never in a loop — it calls
 * `useAllocationWarnings` internally.
 *
 * @param {ComparisonAgreement | null} agreement - The agreement to show, or null for an empty column.
 * @param {AllocationMethod} method - Which method's values and factors to read.
 * @return {ComparisonColumn} Derived rows, totals, variance and GIS validation warnings.
 */
export const useComparisonColumn = (
  agreement: ComparisonAgreement | null,
  method: AllocationMethod
): ComparisonColumn => {
  const manager = agreement?.allocationManager ?? null;
  const isAcreage = method === 'acreage';

  const rows = useMemo<ComparisonTractRow[]>(
    () =>
      (manager?.lineItems ?? []).map((item) => ({
        tractName: item.tractName,
        value: isAcreage
          ? item.unitAllocationTractAcreageValue
          : item.unitAllocationTractLateralValue,
        factor: isAcreage
          ? item.unitAllocationTractAcreageFactor
          : item.unitAllocationTractLateralFactor,
        gisValue: isAcreage
          ? item.gisAcres
          : item.unitAllocationTractLateralGisValue,
        geometry: item.geometry,
      })),
    [manager, isAcreage]
  );

  const warningTotals = useMemo<AllocationTotals | null>(() => {
    if (!manager) return null;
    return {
      acreageValue: manager.totalAcreageSum ?? 0,
      acreageFactor: manager.totalAcreageFactor ?? 0,
      acreageGisValue: manager.totalGisAcreageSum ?? 0,
      lateralValue: manager.totalLateralSum ?? 0,
      lateralFactor: manager.totalLateralFactor ?? 0,
      lateralGisValue: manager.totalGisLateralSum ?? 0,
    };
  }, [manager]);

  const warnings = useAllocationWarnings(
    manager?.lineItems ?? [],
    warningTotals,
    !isAcreage,
    manager?.unitPlatGrossAcres ?? 0,
    manager?.wellPlatTotalLateralLength ?? 0,
    manager?.totalGisAcreageSourced ?? 0,
    manager?.totalGisLateralSourced ?? 0
  );

  const totalValueSum = isAcreage
    ? (manager?.totalAcreageSum ?? null)
    : (manager?.totalLateralSum ?? null);
  const totalGisValueSum = isAcreage
    ? (manager?.totalGisAcreageSum ?? null)
    : (manager?.totalGisLateralSum ?? null);
  const totalFactor = isAcreage
    ? (manager?.totalAcreageFactor ?? null)
    : (manager?.totalLateralFactor ?? null);
  const sourcedValue = isAcreage
    ? (manager?.totalGisAcreageSourced ?? null)
    : (manager?.totalGisLateralSourced ?? null);

  // The plat/sourced figure. Deliberately not defaulted to 0: an absent total is
  // unknown, not zero, and a zero would render as a 100% variance breach.
  const platValue = isAcreage
    ? (manager?.unitPlatGrossAcres ?? null)
    : (manager?.wellPlatTotalLateralLength ?? null);

  // "Sourced vs GIS" measures how far the unit's GIS figure sits from the plat
  // figure, which is the pair useAllocationWarnings uses for the validation tag
  // rendered beside this number. It previously compared the tract GIS sum against
  // the unit GIS total — GIS against GIS — under this same label.
  //
  // KNOWN AND DELIBERATE: this shares its operands with the validation tag rendered
  // beside it, but NOT its denominator. Here the difference is normalised by the
  // plat value; useAllocationWarnings normalises rule 1 by the sourced GIS value
  // (`|plat - gis| > gis * 0.05`). In a narrow band the two disagree — plat 105.2
  // against sourced 100 breaches the warning while this renders -4.94% — so
  // "Threshold breached" can sit beside a sub-5% number.
  //
  // Not unified here on purpose: the 5%-of-GIS rule is the documented validation and
  // allocation-tracts-table renders it from the same hook, so changing the
  // denominator would move a product threshold on a surface this PR does not own.
  // The band is pinned by a boundary example in __test__/hooks.test.ts.
  //
  // A zero or absent plat value has no percentage to express, so report no variance
  // rather than Infinity.
  const rawVariance =
    sourcedValue !== null && platValue !== null && platValue !== 0
      ? ((sourcedValue - platValue) / platValue) * 100
      : null;

  // Anything below the two decimals we display is zero as far as the reader is
  // concerned; without this, a variance a hair under zero renders as "-0.00%".
  const variancePercent =
    rawVariance !== null && Math.abs(rawVariance) < 0.005 ? 0 : rawVariance;

  return {
    agreement,
    method,
    unitLabel: isAcreage ? 'ac' : 'ft',
    rows,
    totalValueSum,
    totalGisValueSum,
    totalFactor,
    sourcedValue,
    platValue,
    variancePercent,
    warnings,
  };
};
